import { useEffect, useMemo, useRef, useState, type PointerEvent, type ReactNode } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { AlignCenter, AlignJustify, AlignLeft, AlignRight } from "lucide-react";
import { api, type CarouselBgCandProgress, type CarouselItem, type CarouselSlide, type CarouselSlideLayout, type CarouselSlideLayoutBox } from "@/lib/api";
import {
  applyStyleMark,
  editorOffsets,
  editorPlainText,
  highlightCss,
  invertStylePatch,
  isClearStylePatch,
  marksToHtml,
  rangeIsStyled,
  remapMarksOnEdit,
  selectionOrAll,
  setEditorOffsets,
  type StylePatch,
  type TextMark,
} from "@/lib/carouselTextMarks";
import FontFaces, { fontStack } from "@/components/FontFaces";
import FontPicker from "@/components/FontPicker";
import BgPresetPicker from "@/components/BgPresetPicker";
import BgPlacementPicker from "@/components/BgPlacementPicker";
import BgCandidatesProgress from "@/components/BgCandidatesProgress";
import PhotoPickerModal from "@/components/PhotoPickerModal";
import ReelClipModal from "@/components/ReelClipModal";
import { Button } from "@/components/ui/button";
import {
  buildTemplatePatchFromPreset,
  committedPresetId,
} from "@/lib/carouselStyleApply";
import { STUDIO_TIPS } from "@/lib/carouselStudioMeta";
import { cn } from "@/lib/utils";
import { OpRoleBadge } from "@/components/op/OpRoleBadge";
import { OP_SURFACE } from "@/lib/opSurfaces";

const INK_SWATCHES = ["#281f1c", "#b5614e", "#5c4033", "#3f4a3c", "#ffffff", "#f3efe6"];
const ARROW_SWATCHES = ["#281f1c", "#b5614e", "#5c4033", "#3f4a3c", "#ffffff", "#f3efe6", "#d4a017", "#1f3a5f"];
type PropagateParamId = "font" | "color" | "size" | "position";
const PROPAGATE_PARAM_OPTIONS: { id: PropagateParamId; label: string }[] = [
  { id: "font", label: "Font" },
  { id: "color", label: "Kolor" },
  { id: "size", label: "Wielkość" },
  { id: "position", label: "Umiejscowienie" },
];
const DEFAULT_PROPAGATE_PARAMS: Record<PropagateParamId, boolean> = {
  font: true,
  color: true,
  size: true,
  position: true,
};
const ARROW_STYLES = [
  { id: "soft_chevron", label: "Szewron miękki" },
  { id: "ink_double", label: "Podwójny atrament" },
  { id: "brush", label: "Pędzel" },
  { id: "quill", label: "Pióro" },
  { id: "ink_wave", label: "Fala" },
  { id: "hand_arc", label: "Łuk ręczny" },
  { id: "fat_swoosh", label: "Zamasz" },
  { id: "ink_hook", label: "Haczyk" },
  { id: "ink_scribble", label: "Bazgroł" },
  { id: "brush_line", label: "Kreska pędzla" },
  { id: "sketch_wedge", label: "Klin szkic" },
  { id: "ribbon", label: "Wstążka" },
  { id: "classic_ink", label: "Klasyczna" },
  { id: "blade", label: "Ostrze" },
  { id: "curl", label: "Zawijas" },
  { id: "double_quill", label: "Podwójne pióro" },
  { id: "slant_sketch", label: "Skos" },
  { id: "fat_marker", label: "Marker" },
  { id: "thin_line", label: "Cienka" },
  { id: "ornate", label: "Ozdobna" },
] as const;
const ASSET_ARROW_IDS = new Set(ARROW_STYLES.map((row) => row.id));
const LEGACY_ARROW_ASSET: Record<string, string> = {
  chevron: "soft_chevron",
  filled: "sketch_wedge",
  line: "brush_line",
  double: "ink_double",
  wiggle: "ink_wave",
  sketch: "slant_sketch",
  curve: "hand_arc",
  swoosh: "fat_swoosh",
  hook: "ink_hook",
  rough: "ink_scribble",
};
const MARK_STYLE_IDS = new Set(["check", "star", "heart", "plus", "circle", "dot", "x"]);

function resolveArrowAssetId(style: string): string {
  const raw = String(style || "").trim().toLowerCase();
  if (ASSET_ARROW_IDS.has(raw as (typeof ARROW_STYLES)[number]["id"])) return raw;
  return LEGACY_ARROW_ASSET[raw] || "ink_double";
}
const MARK_STYLES = [
  { id: "check", label: "Ptaszek" },
  { id: "star", label: "Gwiazda" },
  { id: "heart", label: "Serce" },
  { id: "plus", label: "Plus" },
  { id: "circle", label: "Okrąg" },
  { id: "dot", label: "Kropka" },
  { id: "x", label: "Krzyżyk" },
] as const;
const ROLE_LABEL: Record<string, string> = {
  arrow: "ikona",
  icon: "ikona",
  heading: "hasło",
  body: "opis",
  num: "numer",
};

const CANVAS_W = 1080;
const CANVAS_H = 1350;
const FB_CROP_Y = (CANVAS_H - CANVAS_W) / 2;
const MIN_BOX_W = 80;
const MIN_BOX_H = 40;
const MIN_ARROW = 40;
const SNAP_PX = 22;
const MID_X = CANVAS_W / 2;
const MID_Y = CANVAS_H / 2;
const HANDLES = ["n", "s", "e", "w", "ne", "nw", "se", "sw"] as const;
type Handle = (typeof HANDLES)[number];

function cloneLayout(layout?: CarouselSlideLayout | null): CarouselSlideLayout {
  return JSON.parse(JSON.stringify(layout || { w: CANVAS_W, h: CANVAS_H, bg: { x: 0, y: 0, scale: 1 }, boxes: [] }));
}

function isIconBox(box?: Partial<CarouselSlideLayoutBox> | null) {
  const role = String(box?.role || "");
  const id = String(box?.id || "");
  return role === "arrow" || role === "icon" || id === "arrow" || id.startsWith("icon-");
}

const DEFAULT_PANEL_OPACITY = 90;
const PAPER_RGB = { r: 248, g: 245, b: 239 };

function panelOpacity(box?: Partial<CarouselSlideLayoutBox> | null) {
  if (!box?.panel) return 0;
  const raw = Number(box.panel_opacity);
  if (!Number.isFinite(raw)) return DEFAULT_PANEL_OPACITY;
  return Math.max(0, Math.min(100, Math.round(raw)));
}

function panelPreviewFill(box: CarouselSlideLayoutBox) {
  if (isIconBox(box) || !box.panel) return "transparent";
  const raw = String(box.panel_color || "#f8f5ef").replace("#", "");
  const rgb =
    raw.length === 6 && /^[0-9a-fA-F]+$/.test(raw)
      ? {
          r: parseInt(raw.slice(0, 2), 16),
          g: parseInt(raw.slice(2, 4), 16),
          b: parseInt(raw.slice(4, 6), 16),
        }
      : PAPER_RGB;
  return `rgba(${rgb.r}, ${rgb.g}, ${rgb.b}, ${panelOpacity(box) / 100})`;
}

function isNumBox(box?: Partial<CarouselSlideLayoutBox> | null) {
  return String(box?.id || "") === "num" || String(box?.role || "") === "num";
}

function boxRotate(box?: Partial<CarouselSlideLayoutBox> | null) {
  const raw = Number(box?.rotate || 0);
  if (!Number.isFinite(raw)) return 0;
  const deg = Math.round(raw) % 360;
  return deg < 0 ? deg + 360 : deg;
}

function wantsContinueArrow(slide: CarouselSlide, total: number) {
  const index = Number(slide.index_n || 0);
  if (index < 1) return false;
  if (total > 0) return index < total;
  return String(slide.slide_type || "").toLowerCase() !== "close";
}

function nextIconId(boxes: CarouselSlideLayoutBox[] | undefined) {
  const used = new Set((boxes || []).map((box) => String(box.id || "")));
  if (!used.has("arrow")) return "arrow";
  let n = 2;
  while (used.has(`icon-${n}`)) n += 1;
  return `icon-${n}`;
}

function defaultIconBox(boxes: CarouselSlideLayoutBox[] | undefined, color = "#b5614e"): CarouselSlideLayoutBox {
  const id = nextIconId(boxes);
  const offset = Math.max(0, (boxes || []).filter(isIconBox).length) * 36;
  return {
    id,
    role: id === "arrow" ? "arrow" : "icon",
    text: "",
    x: Math.min(CANVAS_W - 220, 700 + offset),
    y: Math.max(40, 1180 - offset),
    w: 200,
    h: 120,
    color,
    arrow_style: "ink_double",
    rotate: 0,
    panel: false,
    visible: true,
  };
}

function withContinueArrow(layout: CarouselSlideLayout, slide: CarouselSlide, total: number): CarouselSlideLayout {
  const boxes = layout.boxes || [];
  const hasArrow = boxes.some((box) => String(box.role || box.id || "") === "arrow");
  const want = wantsContinueArrow(slide, total);
  if (want && !hasArrow) return { ...layout, boxes: [...boxes, defaultIconBox(boxes)] };
  if (!want && hasArrow) {
    return { ...layout, boxes: boxes.filter((box) => String(box.role || box.id || "") !== "arrow") };
  }
  return layout;
}

function splitBulletPrefix(raw: string): { bullet: boolean; rest: string; prefixLen: number } {
  const text = String(raw || "");
  if (text.length >= 2 && (text[0] === "-" || text[0] === "*") && text[1] === " ") {
    return { bullet: true, rest: text.slice(2), prefixLen: 2 };
  }
  return { bullet: false, rest: text, prefixLen: 0 };
}

function normalizeBulletIcon(value?: string) {
  const raw = String(value || "").trim().toLowerCase();
  return MARK_STYLES.some((row) => row.id === raw) ? raw : "dot";
}

function bulletSlotPreview(size: number) {
  const lineH = Math.max(12, Math.round(Math.max(12, size) * 1.32));
  const icon = Math.max(10, Math.round(lineH * 0.7));
  const gap = Math.max(6, Math.round(Math.max(12, size) * 0.2));
  return { icon, indent: icon + gap, lineH };
}

function toggleBulletLines(text: string, start = 0, end = text.length) {
  const lines = String(text || "").split("\n");
  let cursor = 0;
  const selected: number[] = [];
  const lo = Math.min(start, end);
  const hi = Math.max(start, end);
  for (let i = 0; i < lines.length; i += 1) {
    const lineStart = cursor;
    const lineEnd = cursor + lines[i].length;
    const hit = hi > lo ? lineEnd >= lo && lineStart < hi : lo >= lineStart && lo <= lineEnd;
    if (hit) selected.push(i);
    cursor = lineEnd + 1;
  }
  const targets = (selected.length ? selected : lines.map((_, i) => i)).filter((i) => lines[i].trim().length > 0);
  const allBullets = targets.length > 0 && targets.every((i) => splitBulletPrefix(lines[i]).bullet);
  return lines
    .map((line, i) => {
      if (!targets.includes(i)) return line;
      const parsed = splitBulletPrefix(line);
      if (allBullets) return parsed.rest;
      return parsed.bullet ? line : `- ${line}`;
    })
    .join("\n");
}

function isListIconBox(box: CarouselSlideLayoutBox): boolean {
  const match = /^icon-(\d+)$/.exec(String(box.id || ""));
  if (!match) return false;
  const n = Number(match[1]);
  return n >= 1 && n <= 8;
}

function peelHeadingBullets(boxes: CarouselSlideLayoutBox[]): CarouselSlideLayoutBox[] {
  const heading = boxes.find((box) => box.id === "heading" || box.role === "heading");
  if (!heading) return boxes;
  const lines = String(heading.text || "").split("\n");
  const cut = lines.findIndex((line, i) => i > 0 && splitBulletPrefix(line).bullet);
  if (cut < 1) return boxes;
  heading.text = lines.slice(0, cut).join("\n").trimEnd();
  const moved = lines.slice(cut).join("\n").trim();
  if (!moved) return boxes;
  const body = boxes.find((box) => box.id === "body" || box.role === "body");
  if (body) {
    body.text = [moved, String(body.text || "").trim()].filter(Boolean).join("\n");
    return boxes;
  }
  boxes.push({
    ...heading,
    id: "body",
    role: "body",
    text: moved,
    size: 48,
    bold: false,
    y: Number(heading.y || 0) + Number(heading.h || 80) + 28,
  });
  return boxes;
}

function mergeQuoteBoxes(boxes: CarouselSlideLayoutBox[]): CarouselSlideLayoutBox[] {
  const peeled = peelHeadingBullets(boxes);
  const quotes = peeled
    .filter((box) => String(box.id || "").startsWith("quote-"))
    .sort((a, b) => String(a.id || "").localeCompare(String(b.id || "")));
  if (!quotes.length) return peeled;
  const body = peeled.find((box) => box.id === "body" || box.role === "body");
  const extra = quotes.map((box) => String(box.text || "").trim()).filter(Boolean);
  const next = peeled.filter((box) => !String(box.id || "").startsWith("quote-"));
  const joined = [String(body?.text || "").trim(), ...extra].filter(Boolean).join("\n");
  if (body) {
    body.id = "body";
    body.role = "body";
    body.text = joined;
    return next;
  }
  if (joined && quotes[0]) {
    next.push({ ...quotes[0], id: "body", role: "body", text: joined });
  }
  return next;
}

function prepareLayout(
  layout: CarouselSlideLayout | null | undefined,
  slide: CarouselSlide,
  total = 0,
  bulletIcon?: string,
): CarouselSlideLayout {
  const next = withContinueArrow(cloneLayout(layout), slide, total);
  const icon = normalizeBulletIcon(bulletIcon || next.bullet_icon);
  const stripped = (next.boxes || []).filter((box) => !isListIconBox(box));
  const boxes = mergeQuoteBoxes(stripped);
  return {
    ...next,
    bullet_icon: icon,
    boxes: boxes.map((box) => {
      const grown = growBoxToText(box);
      const id = String(grown.id || "");
      const line = id === "body" || grown.role === "body";
      const aligned = line ? { ...grown, x: 100, w: 880 } : grown;
      if (isIconBox(aligned)) return aligned;
      return { ...aligned, bullet_icon: icon };
    }),
  };
}

function clampBox(box: Partial<CarouselSlideLayoutBox>, minW = MIN_BOX_W, minH = MIN_BOX_H): Partial<CarouselSlideLayoutBox> {
  let x = Number(box.x ?? 0);
  let y = Number(box.y ?? 0);
  let w = Math.max(minW, Number(box.w ?? minW));
  let h = Math.max(minH, Number(box.h ?? minH));
  if (x < 0) {
    w += x;
    x = 0;
  }
  if (y < 0) {
    h += y;
    y = 0;
  }
  if (x + w > CANVAS_W) w = CANVAS_W - x;
  return { ...box, x: Math.round(x), y: Math.round(y), w: Math.max(minW, Math.round(w)), h: Math.max(minH, Math.round(h)) };
}

function snapBoxToCenter(
  box: { x: number; y: number; w: number; h: number },
  enabled: boolean,
): { x: number; y: number; snapX: boolean; snapY: boolean } {
  if (!enabled) return { x: box.x, y: box.y, snapX: false, snapY: false };
  const cx = box.x + box.w / 2;
  const cy = box.y + box.h / 2;
  let x = box.x;
  let y = box.y;
  let snapX = false;
  let snapY = false;
  if (Math.abs(cx - MID_X) <= SNAP_PX) {
    x = Math.round(MID_X - box.w / 2);
    snapX = true;
  }
  if (Math.abs(cy - MID_Y) <= SNAP_PX) {
    y = Math.round(MID_Y - box.h / 2);
    snapY = true;
  }
  return { x: Math.round(x), y: Math.round(y), snapX, snapY };
}

function bgCoverPreviewSize(natW: number, natH: number, userScale: number, previewScale: number) {
  const nw = Math.max(1, natW);
  const nh = Math.max(1, natH);
  const zoom = Number(userScale || 1) || 1;
  const cover = Math.max(CANVAS_W / nw, CANVAS_H / nh);
  return {
    width: nw * cover * zoom * previewScale,
    height: nh * cover * zoom * previewScale,
  };
}

function handleStyle(handle: Handle): { left?: string; right?: string; top?: string; bottom?: string; cursor: string } {
  const mid = "calc(50% - 5px)";
  const map: Record<Handle, { left?: string; right?: string; top?: string; bottom?: string; cursor: string }> = {
    n: { left: mid, top: "-5px", cursor: "ns-resize" },
    s: { left: mid, bottom: "-5px", cursor: "ns-resize" },
    e: { right: "-5px", top: mid, cursor: "ew-resize" },
    w: { left: "-5px", top: mid, cursor: "ew-resize" },
    ne: { right: "-5px", top: "-5px", cursor: "nesw-resize" },
    nw: { left: "-5px", top: "-5px", cursor: "nwse-resize" },
    se: { right: "-5px", bottom: "-5px", cursor: "nwse-resize" },
    sw: { left: "-5px", bottom: "-5px", cursor: "nesw-resize" },
  };
  return map[handle];
}

function fontFamilyCss(font?: string) {
  return fontStack(font);
}

function countPreviewLines(text: string, width: number, size: number, font?: string, bold?: boolean) {
  const parts = String(text || "").replace(/\r/g, "").split("\n");
  if (typeof document === "undefined") return Math.max(1, parts.length);
  const ctx = document.createElement("canvas").getContext("2d");
  if (!ctx) return Math.max(1, parts.length);
  ctx.font = `${bold ? "700" : "400"} ${size}px ${fontFamilyCss(font)}`;
  const indent = bulletSlotPreview(size).indent;
  let lines = 0;
  for (const raw of parts) {
    const parsed = splitBulletPrefix(raw);
    const src = parsed.bullet ? parsed.rest : raw;
    const lineW = parsed.bullet ? Math.max(8, width - indent) : width;
    const words = src.split(" ");
    if (!src.length) {
      lines += 1;
      continue;
    }
    let cur = "";
    for (const word of words) {
      const trial = cur ? `${cur} ${word}` : word;
      if (cur && ctx.measureText(trial).width > lineW) {
        lines += 1;
        cur = word;
      } else {
        cur = trial;
      }
    }
    if (cur) lines += 1;
  }
  return Math.max(1, lines);
}

function estimatePreviewBoxH(box: CarouselSlideLayoutBox) {
  const size = Math.max(12, Number(box.size || 44));
  const width = Math.max(8, Number(box.w || 40));
  const lineH = Math.round(size * 1.32);
  const lines = countPreviewLines(String(box.text || ""), width, size, box.font, Boolean(box.bold));
  return Math.max(24, lines * lineH + 8);
}

function growBoxToText(box: CarouselSlideLayoutBox): CarouselSlideLayoutBox {
  if (isIconBox(box) || !String(box.text || "").trim()) return box;
  const needed = estimatePreviewBoxH(box);
  if (needed <= Number(box.h || 0)) return box;
  return { ...box, h: needed };
}

function normalizeHex(value?: string) {
  const raw = String(value || "#281f1c").trim();
  return /^#[0-9a-fA-F]{6}$/.test(raw) ? raw : "#281f1c";
}

export default function SlideComposePanel({
  item,
  slide,
  fallback,
  geminiReady = false,
  visualOnly = false,
  onClose,
}: {
  item: CarouselItem;
  slide: CarouselSlide;
  fallback: boolean;
  geminiReady?: boolean;
  visualOnly?: boolean;
  onClose: () => void;
}) {
  const qc = useQueryClient();
  const fontsQ = useQuery({ queryKey: ["fonts"], queryFn: () => api.fontsList() });
  const fonts = fontsQ.data?.items || [];
  const catalogQ = useQuery({ queryKey: ["carousel-catalog"], queryFn: () => api.carouselCatalog() });
  const bgPresets = catalogQ.data?.bg_presets || [];
  const bgPlacements = catalogQ.data?.bg_placements || [];
  const [bgPresetId, setBgPresetId] = useState(() => item.bg_preset_id || "");
  const [bgPlacementId, setBgPlacementId] = useState(() => item.bg_placement_id || "");
  const [styleApplyMode, setStyleApplyMode] = useState<"bg" | "full" | "layout">(
    () =>
      item.style_apply_mode === "full" || item.style_apply_mode === "layout"
        ? item.style_apply_mode
        : "bg",
  );
  /** Lokalny wybór kafelka; zapis + recompose dopiero po przycisku Zastosuj layout/tło. */
  const [pickedPresetId, setPickedPresetId] = useState<string | null>(null);
  const [bgCandidates, setBgCandidates] = useState<{ k: number; url: string }[]>([]);
  const [bgCandBust, setBgCandBust] = useState(0);
  const [bgCandProgress, setBgCandProgress] = useState<CarouselBgCandProgress | null>(
    () => item.bg_cand_progress || null,
  );
  const bgCandStartedLocal = useRef<string>("");
  // Preferuj dostępne gwiazdki na górze listy (FontPicker filtruje dalej).
  const composeFonts = useMemo(() => {
    const starred = fonts.filter((row) => row.starred);
    if (!starred.length) return fonts;
    const rest = fonts.filter((row) => !row.starred);
    return [...starred, ...rest];
  }, [fonts]);
  const slideTotal = item.slides?.length || 0;
  const [layout, setLayout] = useState<CarouselSlideLayout>(() =>
    prepareLayout(slide.layout, slide, slideTotal, item.bullet_icon),
  );
  const [selectedId, setSelectedId] = useState<string>(
    () =>
      prepareLayout(slide.layout, slide, slideTotal, item.bullet_icon).boxes?.find((b) => b.role === "heading")?.id ||
      slide.layout?.boxes?.[0]?.id ||
      "",
  );
  const [mode, setMode] = useState<"text" | "bg">("text");
  const [bgApply, setBgApply] = useState<"one" | "all">("one");
  const [bgHint, setBgHint] = useState(() => item.bg_hint || "");
  const [pickerOpen, setPickerOpen] = useState(false);
  const [reelOpen, setReelOpen] = useState(false);
  const [bust, setBust] = useState(() => Date.now());
  const stageRef = useRef<HTMLDivElement | null>(null);
  const editorRef = useRef<HTMLDivElement | null>(null);
  const lastSel = useRef({ start: 0, end: 0 });
  const skipEditorInput = useRef(false);
  const composing = useRef(false);
  const latestBox = useRef<{ id: string; text: string; marks: TextMark[] }>({ id: "", text: "", marks: [] });
  const [snapCenter, setSnapCenter] = useState(true);
  const [propagateParams, setPropagateParams] = useState(DEFAULT_PROPAGATE_PARAMS);
  const [guides, setGuides] = useState({ x: false, y: false });
  const [bgNat, setBgNat] = useState<{ w: number; h: number } | null>(null);
  const drag = useRef<{
    kind: "box" | "bg" | "resize";
    id?: string;
    handle?: Handle;
    sx: number;
    sy: number;
    ox: number;
    oy: number;
    ow: number;
    oh: number;
  } | null>(null);

  const scale = useMemo(() => {
    return 360 / CANVAS_W;
  }, []);

  const selected = (layout.boxes || []).find((b) => b.id === selectedId);
  const usedFonts = fonts.filter(
    (row) => row.id === selected?.font || (layout.boxes || []).some((box) => box.font === row.id),
  );
  function boxFontCss(font?: string) {
    return fontStack(fonts.find((row) => row.id === font) || font);
  }

  const saveM = useMutation({
    mutationFn: () => api.carouselPutSlideLayout(item.id, slide.index_n, layout, bgApply),
    onSuccess: (next) => {
      const row = (next.slides || []).find((s) => s.index_n === slide.index_n);
      if (row?.layout) setLayout(prepareLayout(row.layout, row, slideTotal, next.bullet_icon));
      setBust(Date.now());
      qc.setQueryData(["carousel", item.id], next);
    },
  });
  const resetM = useMutation({
    mutationFn: () => api.carouselResetSlideLayout(item.id, slide.index_n),
    onSuccess: (next) => {
      const row = (next.slides || []).find((s) => s.index_n === slide.index_n);
      if (row?.layout) setLayout(prepareLayout(row.layout, row, slideTotal, next.bullet_icon));
      setBust(Date.now());
      qc.invalidateQueries({ queryKey: ["carousel", item.id] });
    },
  });
  const mediaM = useMutation({
    mutationFn: ({ assetId, photoMode }: { assetId: string; photoMode: string }) =>
      api.carouselAttachMedia(item.id, slide.index_n, { asset_id: assetId, mode: photoMode }, fallback, bgApply),
    onSuccess: (next) => {
      const row = (next.slides || []).find((s) => s.index_n === slide.index_n);
      if (row?.layout) setLayout(prepareLayout(row.layout, row, slideTotal, next.bullet_icon));
      setBust(Date.now());
      setPickerOpen(false);
      qc.invalidateQueries({ queryKey: ["carousel", item.id] });
    },
  });
  const propagateM = useMutation({
    mutationFn: (payload: { mode: "apply" | "add"; params?: PropagateParamId[] }) =>
      api.carouselPropagateLayout(item.id, slide.index_n, {
        box_id: selectedId,
        mode: payload.mode,
        layout,
        ...(payload.mode === "apply" && payload.params ? { params: payload.params } : {}),
      }),
    onSuccess: (next) => {
      const row = (next.slides || []).find((s) => s.index_n === slide.index_n);
      if (row?.layout) setLayout(prepareLayout(row.layout, row, slideTotal, next.bullet_icon));
      setBust(Date.now());
      qc.setQueryData(["carousel", item.id], next);
      qc.invalidateQueries({ queryKey: ["carousel", item.id] });
    },
  });
  const bgM = useMutation({
    mutationFn: (payload?: File | { forceGemini?: boolean; hint?: string }) => {
      if (payload instanceof File) {
        return api.carouselUpdateSlideBackground(item.id, slide.index_n, {
          file: payload,
          fallback,
          apply: bgApply,
        });
      }
      if (payload?.forceGemini) {
        return api.carouselUpdateSlideBackground(item.id, slide.index_n, {
          forceGemini: true,
          hint: payload.hint ?? bgHint,
          apply: bgApply,
        });
      }
      return api.carouselUpdateSlideBackground(item.id, slide.index_n, { fallback, apply: bgApply });
    },
    onSuccess: (next) => {
      const row = (next.slides || []).find((s) => s.index_n === slide.index_n);
      if (row?.layout) setLayout(prepareLayout(row.layout, row, slideTotal, next.bullet_icon));
      setBust(Date.now());
      setBgCandidates([]);
      qc.invalidateQueries({ queryKey: ["carousel", item.id] });
    },
  });
  const styleApplyM = useMutation({
    mutationFn: () => {
      const id = pickedPresetId ?? "";
      const preset = bgPresets.find((row) => row.id === id);
      return api.carouselPatchTemplate(item.id, buildTemplatePatchFromPreset(styleApplyMode, id, preset));
    },
    onSuccess: (next) => {
      setPickedPresetId(null);
      setBgPresetId(next.bg_preset_id || "");
      if (
        next.style_apply_mode === "full" ||
        next.style_apply_mode === "bg" ||
        next.style_apply_mode === "layout"
      ) {
        setStyleApplyMode(next.style_apply_mode);
      }
      const row = (next.slides || []).find((s) => s.index_n === slide.index_n);
      if (row?.layout) {
        setLayout(prepareLayout(row.layout, row, slideTotal, next.bullet_icon));
      }
      setBust(Date.now());
      if (next.style_apply_mode === "full") setBgCandidates([]);
      qc.setQueryData(["carousel", item.id], next);
      qc.invalidateQueries({ queryKey: ["carousel", item.id] });
    },
  });
  const bgCandM = useMutation({
    mutationFn: () =>
      api.carouselBackgroundCandidates(item.id, slide.index_n, { count: 3, hint: bgHint }),
    onMutate: () => {
      bgCandStartedLocal.current = new Date().toISOString();
      setBgCandProgress({
        started_at: bgCandStartedLocal.current,
        variant_started_at: bgCandStartedLocal.current,
        done: 0,
        total: 3,
        current: 1,
        phase: "waiting",
        detail: "Start: 3 warianty tła",
        percent: 1,
        samples: item.bg_cand_progress?.samples || 0,
        expected_ms_per_variant: item.bg_cand_progress?.expected_ms_per_variant || 10_000,
        job_expected_ms: (item.bg_cand_progress?.expected_ms_per_variant || 10_000) * 3,
      });
    },
    onSuccess: (res) => {
      setBgCandidates((res.candidates || []).map((c) => ({ k: c.k, url: c.url })));
      setBgCandBust(Date.now());
      if (res.progress) setBgCandProgress(res.progress);
      else
        setBgCandProgress((prev) =>
          prev
            ? { ...prev, phase: "done", done: 3, total: 3, percent: 100, detail: "Gotowe" }
            : prev,
        );
      qc.invalidateQueries({ queryKey: ["carousel", item.id] });
    },
    onError: () => {
      setBgCandProgress((prev) =>
        prev ? { ...prev, phase: "error", detail: "Generacja przerwana" } : prev,
      );
    },
  });
  useEffect(() => {
    if (!bgCandM.isPending) return;
    let cancelled = false;
    const tick = async () => {
      try {
        const next = await api.carouselGet(item.id);
        if (cancelled) return;
        if (next.bg_cand_progress) setBgCandProgress(next.bg_cand_progress);
        qc.setQueryData(["carousel", item.id], next);
      } catch {
        /* poll best-effort */
      }
    };
    void tick();
    const id = window.setInterval(() => void tick(), 700);
    return () => {
      cancelled = true;
      window.clearInterval(id);
    };
  }, [bgCandM.isPending, item.id, qc]);
  useEffect(() => {
    if (item.bg_cand_progress && !bgCandM.isPending) {
      setBgCandProgress(item.bg_cand_progress);
    }
  }, [item.bg_cand_progress, bgCandM.isPending]);
  const bgCandApplyM = useMutation({
    mutationFn: (k: number) =>
      api.carouselBackgroundCandidateApply(item.id, slide.index_n, {
        candidate_k: k,
        apply: bgApply,
      }),
    onSuccess: (next) => {
      const row = (next.slides || []).find((s) => s.index_n === slide.index_n);
      if (row?.layout) setLayout(prepareLayout(row.layout, row, slideTotal, next.bullet_icon));
      setBust(Date.now());
      setBgCandidates([]);
      qc.setQueryData(["carousel", item.id], next);
      qc.invalidateQueries({ queryKey: ["carousel", item.id] });
    },
  });
  useEffect(() => {
    setBgHint(item.bg_hint || "");
    setBgPresetId(item.bg_preset_id || "");
    setBgPlacementId(item.bg_placement_id || "");
    setStyleApplyMode(
      item.style_apply_mode === "full" || item.style_apply_mode === "layout"
        ? item.style_apply_mode
        : "bg",
    );
    setPickedPresetId(null);
  }, [item.id, item.bg_hint, item.bg_preset_id, item.bg_placement_id, item.style_apply_mode]);

  useEffect(() => {
    if (!slide.id) return;
    if (!slide.layout?.boxes?.length) {
      resetM.mutate();
      return;
    }
    const next = prepareLayout(slide.layout, slide, slideTotal, item.bullet_icon);
    setLayout(next);
    setSelectedId((current) => {
      if (current && next.boxes?.some((b) => b.id === current)) return current;
      return next.boxes?.find((b) => b.role === "heading")?.id || next.boxes?.[0]?.id || "";
    });
  }, [slide.id, slide.index_n]);

  const committedStylePresetId = committedPresetId(
    styleApplyMode,
    bgPresets,
    bgPresetId || item.bg_preset_id || "",
    item.style_id,
    item.visual_pack_id,
    item.layout_preset_id,
  );
  const galleryPresetId = pickedPresetId ?? committedStylePresetId;
  const stylePresetDirty = pickedPresetId !== null && pickedPresetId !== committedStylePresetId;

  const busy =
    saveM.isPending ||
    resetM.isPending ||
    bgM.isPending ||
    bgCandM.isPending ||
    bgCandApplyM.isPending ||
    mediaM.isPending ||
    propagateM.isPending ||
    styleApplyM.isPending;
  const otherSlides = (item.slides || []).filter((row) => row.index_n !== slide.index_n).length;
  const selectedPropagateParams = PROPAGATE_PARAM_OPTIONS.filter((row) => propagateParams[row.id]).map(
    (row) => row.id,
  );
  const bgUrl = `${api.carouselSlideBackground(item.id, slide.index_n)}?v=${bust}`;
  const previewUrl = `${api.carouselSlidePng(item.id, slide.index_n)}?v=${bust}`;
  const transform = layout.bg || { x: 0, y: 0, scale: 1 };

  useEffect(() => {
    setBgNat(null);
  }, [bgUrl]);

  function toCanvas(clientX: number, clientY: number) {
    const el = stageRef.current;
    if (!el) return { x: 0, y: 0 };
    const r = el.getBoundingClientRect();
    return {
      x: ((clientX - r.left) / r.width) * CANVAS_W,
      y: ((clientY - r.top) / r.height) * CANVAS_H,
    };
  }

  function requestGeminiBg() {
    saveM.mutate(undefined, {
      onSuccess: () => bgCandM.mutate(),
    });
  }

  function onPointerDown(e: PointerEvent<HTMLDivElement | HTMLButtonElement>, kind: "box" | "bg", id?: string) {
    e.preventDefault();
    e.stopPropagation();
    const p = toCanvas(e.clientX, e.clientY);
    if (kind === "box" && id) {
      const box = (layout.boxes || []).find((b) => b.id === id);
      setSelectedId(id);
      lastSel.current = { start: 0, end: 0 };
      setMode("text");
      drag.current = {
        kind,
        id,
        sx: p.x,
        sy: p.y,
        ox: box?.x || 0,
        oy: box?.y || 0,
        ow: box?.w || MIN_BOX_W,
        oh: box?.h || MIN_BOX_H,
      };
    } else {
      setMode("bg");
      drag.current = { kind: "bg", sx: p.x, sy: p.y, ox: transform.x || 0, oy: transform.y || 0, ow: 0, oh: 0 };
    }
    (e.currentTarget as HTMLElement).setPointerCapture(e.pointerId);
  }

  function onResizeDown(e: PointerEvent<HTMLSpanElement>, id: string, handle: Handle) {
    e.preventDefault();
    e.stopPropagation();
    const p = toCanvas(e.clientX, e.clientY);
    const box = (layout.boxes || []).find((b) => b.id === id);
    setSelectedId(id);
    setMode("text");
    drag.current = {
      kind: "resize",
      id,
      handle,
      sx: p.x,
      sy: p.y,
      ox: box?.x || 0,
      oy: box?.y || 0,
      ow: box?.w || MIN_BOX_W,
      oh: box?.h || MIN_BOX_H,
    };
    e.currentTarget.setPointerCapture(e.pointerId);
  }

  function onPointerMove(e: PointerEvent<HTMLDivElement>) {
    if (!drag.current) return;
    const p = toCanvas(e.clientX, e.clientY);
    const dx = p.x - drag.current.sx;
    const dy = p.y - drag.current.sy;
    if (drag.current.kind === "bg") {
      patchBg({ x: Math.round(drag.current.ox + dx), y: Math.round(drag.current.oy + dy) });
      return;
    }
    if (drag.current.kind === "resize" && drag.current.id) {
      const current = (layout.boxes || []).find((b) => b.id === drag.current?.id);
      const next = resizeFromHandle(drag.current, dx, dy, isIconBox(current) ? MIN_ARROW : MIN_BOX_W, isIconBox(current) ? MIN_ARROW : MIN_BOX_H);
      const snapped = snapBoxToCenter(
        { x: Number(next.x || 0), y: Number(next.y || 0), w: Number(next.w || 0), h: Number(next.h || 0) },
        snapCenter,
      );
      setGuides({ x: snapped.snapX, y: snapped.snapY });
      patchBox(drag.current.id, { ...next, x: snapped.x, y: snapped.y });
      return;
    }
    const snapped = snapBoxToCenter(
      { x: drag.current.ox + dx, y: drag.current.oy + dy, w: drag.current.ow, h: drag.current.oh },
      snapCenter,
    );
    setGuides({ x: snapped.snapX, y: snapped.snapY });
    patchBox(drag.current.id || "", { x: snapped.x, y: snapped.y });
  }

  function onPointerUp() {
    drag.current = null;
    setGuides({ x: false, y: false });
  }

  function patchBg(partial: { x?: number; y?: number; scale?: number }) {
    setLayout((prev) => ({ ...prev, bg: { ...(prev.bg || {}), ...partial } }));
  }

  function patchBox(id: string, partial: Partial<CarouselSlideLayoutBox>) {
    setLayout((prev) => {
      const isListIcon = id.startsWith("icon-") && id !== "arrow";
      const style = partial.arrow_style;
      const boxes = (prev.boxes || []).map((b) => {
        if (b.id === id) {
          const next = { ...b, ...partial };
          const metrics = ["text", "size", "font", "bold", "italic"].some((key) => key in partial);
          return metrics ? growBoxToText(next) : next;
        }
        if (isListIcon && style && (b.id || "").startsWith("icon-") && b.id !== "arrow") {
          return { ...b, arrow_style: style };
        }
        return b;
      });
      return {
        ...prev,
        boxes,
        ...(isListIcon && style ? { list_icon: style } : {}),
      };
    });
  }

  function paintEditor(text: string, marks?: TextMark[]) {
    const el = editorRef.current;
    if (!el) return;
    skipEditorInput.current = true;
    el.innerHTML = marksToHtml(text, marks);
    skipEditorInput.current = false;
  }

  function rememberSelection() {
    const live = editorOffsets(editorRef.current);
    lastSel.current = live && live.end > live.start ? live : { start: 0, end: 0 };
  }

  function currentSelection() {
    const live = editorOffsets(editorRef.current);
    if (live && live.end > live.start) return live;
    if (lastSel.current.end > lastSel.current.start) return lastSel.current;
    return null;
  }

  function commitEditor() {
    const el = editorRef.current;
    const boxId = selected?.id;
    if (skipEditorInput.current || !boxId || !el) return;
    const nextText = editorPlainText(el);
    const caret = editorOffsets(el) || { start: nextText.length, end: nextText.length };
    const prev =
      latestBox.current.id === boxId
        ? latestBox.current
        : { text: selected?.text || "", marks: (selected?.marks as TextMark[]) || [] };
    const nextMarks = remapMarksOnEdit(prev.text, nextText, prev.marks);
    syncLatest(boxId, nextText, nextMarks);
    lastSel.current = caret;
    patchBox(boxId, { text: nextText, marks: nextMarks });
    paintEditor(nextText, nextMarks);
    requestAnimationFrame(() => {
      setEditorOffsets(el, caret.start, caret.end);
    });
  }

  function syncLatest(id: string, text: string, marks?: TextMark[]) {
    latestBox.current = { id, text, marks: marks || [] };
  }

  function styleSelected(key: "bold" | "italic" | "underline" | "highlight"): boolean {
    const box = selected;
    if (!box?.id) return false;
    const text = latestBox.current.id === box.id ? latestBox.current.text : box.text || "";
    const marks = latestBox.current.id === box.id ? latestBox.current.marks : (box.marks as TextMark[] | undefined);
    const range = selectionOrAll(
      lastSel.current.end > lastSel.current.start ? lastSel.current : null,
      text,
    );
    if (!range) {
      if (key === "bold") return Boolean(box.bold);
      if (key === "highlight") return Boolean(box.highlight);
      return false;
    }
    if (range.start === 0 && range.end === text.length) {
      if (key === "bold" && box.bold) return true;
      if (key === "highlight" && box.highlight) return true;
    }
    return rangeIsStyled(marks, range.start, range.end, key, text);
  }

  function applyToSelection(patch: StylePatch) {
    const box = selected;
    if (!box?.id) return;
    const text = latestBox.current.id === box.id ? latestBox.current.text : box.text || "";
    const marks = latestBox.current.id === box.id ? latestBox.current.marks : (box.marks as TextMark[] | undefined);
    const fragment = currentSelection();
    const range = selectionOrAll(fragment, text);
    const whole = !fragment;
    if (patch.color !== undefined && !isClearStylePatch(patch) && whole) {
      const hex = /^#[0-9a-fA-F]{6}$/.test(patch.color || "") ? patch.color : "";
      const next = text ? applyStyleMark(marks, 0, text.length, { color: "" }, text) : [];
      syncLatest(box.id, text, next);
      patchBox(box.id, { color: hex || "#281f1c", text, marks: next });
      requestAnimationFrame(() => paintEditor(text, next));
      return;
    }
    if (!range) return;
    if (isClearStylePatch(patch)) {
      const next = applyStyleMark(marks, range.start, range.end, patch, text);
      syncLatest(box.id, text, next);
      patchBox(box.id, {
        highlight: whole ? "" : box.highlight || "",
        bold: whole ? false : box.bold,
        color: whole ? "" : box.color,
        text,
        marks: next,
      });
      requestAnimationFrame(() => paintEditor(text, next));
      return;
    }
    const next = applyStyleMark(
      marks,
      range.start,
      range.end,
      invertStylePatch(marks, range.start, range.end, patch, text),
      text,
    );
    syncLatest(box.id, text, next);
    patchBox(box.id, {
      highlight: whole ? "" : box.highlight || "",
      ...(whole && patch.bold !== undefined ? { bold: false } : {}),
      text,
      marks: next,
    });
    requestAnimationFrame(() => {
      paintEditor(text, next);
    });
  }

  function toggleBulletsOnSelected() {
    const box = selected;
    if (!box?.id || isIconBox(box)) return;
    const text = latestBox.current.id === box.id ? latestBox.current.text : box.text || "";
    const marks = latestBox.current.id === box.id ? latestBox.current.marks : (box.marks as TextMark[] | undefined) || [];
    const fragment = currentSelection();
    const nextText = toggleBulletLines(text, fragment?.start ?? 0, fragment?.end ?? text.length);
    const nextMarks = remapMarksOnEdit(text, nextText, marks);
    syncLatest(box.id, nextText, nextMarks);
    lastSel.current = { start: 0, end: 0 };
    patchBox(box.id, { text: nextText, marks: nextMarks });
    requestAnimationFrame(() => paintEditor(nextText, nextMarks));
  }

  useEffect(() => {
    if (!selected) return;
    const text = selected.text || "";
    const marks = (selected.marks as TextMark[] | undefined) || [];
    syncLatest(selected.id || "", text, marks);
    paintEditor(text, marks);
    lastSel.current = { start: 0, end: 0 };
  }, [selectedId]);

  return (
    <div className="flex min-h-0 min-w-0 flex-col rounded-lg border border-border bg-card">
      <div className="shrink-0 border-b border-border px-4 py-3">
        <div className="flex items-start justify-between gap-3">
          <div className="min-w-0">
            <h2 id="slide-compose-title" className="text-base font-medium">
              Skład slajdu {slide.index_n}
            </h2>
            <p className="mt-1 text-sm text-muted-foreground">
              {visualOnly
                ? "Tekst jest zatwierdzony na Przetwarzaniu. Tu nadal poprawiasz słowa na tym slajdzie, plus tło, kadr, pozycję, font, kolor i marker. Zastosuj składa nowy PNG."
                : "B / I / U / marker / kolor: na zaznaczenie, albo na cały aktywny box gdy nic nie zaznaczysz. Font, wielkość i justowanie zawsze na całe okno. Zastosuj składa nowy PNG."}
            </p>
          </div>
          <Button size="sm" variant="outline" className="shrink-0" onClick={onClose}>
            Wróć do listy
          </Button>
        </div>
      </div>
      <div className="min-h-0 min-w-0 flex-1 overflow-auto p-4">
        <FontFaces fonts={usedFonts} />
        <div className="grid min-w-0 gap-4 lg:grid-cols-[360px_minmax(0,1fr)]">
          <div className={cn("min-w-0 space-y-2 p-3", OP_SURFACE.preview.box)}>
            <OpRoleBadge tone="preview" label="Podgląd" />
            <div
              ref={stageRef}
              className="relative overflow-hidden rounded-sm border border-slate-600 bg-muted"
              style={{ width: 360, height: 360 * (CANVAS_H / CANVAS_W) }}
              onPointerMove={onPointerMove}
              onPointerUp={onPointerUp}
              onPointerDown={(e) => mode === "bg" && onPointerDown(e, "bg")}
            >
              <img
                src={bgUrl}
                alt=""
                draggable={false}
                className="absolute max-w-none"
                onLoad={(e) =>
                  setBgNat({
                    w: e.currentTarget.naturalWidth || CANVAS_W,
                    h: e.currentTarget.naturalHeight || CANVAS_H,
                  })
                }
                style={{
                  ...bgCoverPreviewSize(
                    bgNat?.w || CANVAS_W,
                    bgNat?.h || CANVAS_H,
                    Number(transform.scale || 1),
                    scale,
                  ),
                  left: (transform.x || 0) * scale,
                  top: (transform.y || 0) * scale,
                }}
              />
              {(layout.boxes || []).map((box) =>
                box.visible === false ? null : (
                  <div
                    key={box.id}
                    className={cn(
                      "absolute leading-tight",
                      "overflow-visible",
                      selectedId === box.id ? "ring-2 ring-brand" : "ring-1 ring-black/20",
                    )}
                    style={{
                      left: (box.x || 0) * scale,
                      top: (box.y || 0) * scale,
                      width: (box.w || 40) * scale,
                      height: (box.h || 24) * scale,
                    }}
                    onPointerDown={(e) => onPointerDown(e, "box", box.id)}
                  >
                    <div
                      className="w-full overflow-visible"
                      style={{
                        minHeight: "100%",
                        height: "auto",
                        backgroundColor: panelPreviewFill(box),
                        color: box.color || "#281f1c",
                        fontSize: Math.max(8, (box.size || 44) * scale),
                        fontFamily: boxFontCss(box.font),
                        fontWeight: box.bold ? 700 : 400,
                        fontStyle: box.italic ? "italic" : "normal",
                        textDecoration: box.underline ? "underline" : "none",
                        textAlign: (box.align as "left" | "center" | "right" | "justify") || "left",
                        whiteSpace: "pre-wrap",
                        wordBreak: "break-word",
                        transform: `rotate(${boxRotate(box)}deg)`,
                        transformOrigin: "center center",
                      }}
                    >
                      {isIconBox(box) ? (
                        <IconPreview box={box} />
                      ) : (
                        <MarkedPreview
                          text={box.text || ""}
                          marks={box.marks as TextMark[] | undefined}
                          fallbackHighlight={box.highlight}
                          bulletIcon={normalizeBulletIcon(layout.bullet_icon || item.bullet_icon)}
                          color={box.color || "#281f1c"}
                        />
                      )}
                    </div>
                    {selectedId === box.id
                      ? HANDLES.map((handle) => (
                          <span
                            key={handle}
                            className="absolute z-10 h-2.5 w-2.5 rounded-sm border border-white bg-brand shadow"
                            style={handleStyle(handle)}
                            onPointerDown={(e) => onResizeDown(e, box.id || "", handle)}
                          />
                        ))
                      : null}
                  </div>
                ),
              )}
              {guides.x ? (
                <div className="pointer-events-none absolute top-0 z-20 h-full w-px bg-brand/70" style={{ left: 180 }} />
              ) : null}
              {guides.y ? (
                <div
                  className="pointer-events-none absolute left-0 z-20 h-px w-full bg-brand/70"
                  style={{ top: 180 * (CANVAS_H / CANVAS_W) }}
                />
              ) : null}
              <div
                className="pointer-events-none absolute inset-x-0 top-0 z-30 bg-black/30"
                style={{ height: FB_CROP_Y * scale }}
              />
              <div
                className="pointer-events-none absolute inset-x-0 bottom-0 z-30 bg-black/30"
                style={{ height: FB_CROP_Y * scale }}
              />
              <div
                className="pointer-events-none absolute inset-x-0 z-30 border-y border-dashed border-white/80"
                style={{ top: FB_CROP_Y * scale, height: CANVAS_W * scale }}
              />
            </div>
            <label className="mt-2 flex items-center gap-2 text-[11px] text-slate-300">
              <input type="checkbox" checked={snapCenter} onChange={(e) => setSnapCenter(e.target.checked)} />
              Przyciągaj do środka (poziom / pion), gdy box jest blisko osi
            </label>
            <p className="mt-1 text-[11px] text-slate-400">
              Podgląd na żywo. Po Zastosuj odświeża się złożony PNG. Ciemny pas u góry i dołu to crop 1:1 na siatce FB.
            </p>
            {slide.media_kind === "video" ? (
              <p className="mt-1 text-[11px] text-brand">
                Slajd wideo na IG (min. 3 s). FB dostanie ten PNG jako plakat.
              </p>
            ) : null}
            <img src={previewUrl} alt="" className="mt-2 hidden h-0 w-0" />
          </div>
          <div className="min-w-0 space-y-3 text-sm">
            <div className="flex flex-wrap gap-2">
              <Button size="sm" variant={mode === "text" ? "brand" : "outline"} onClick={() => setMode("text")}>
                Tekst
              </Button>
              <Button size="sm" variant={mode === "bg" ? "brand" : "outline"} onClick={() => setMode("bg")}>
                Tło
              </Button>
            </div>
            <div>
              <p className="mb-1 text-xs text-muted-foreground">Zakres tła</p>
              <div className="flex flex-wrap gap-1">
                <button
                  type="button"
                  className={cn(
                    "rounded border px-2 py-0.5 text-[11px]",
                    bgApply === "one" ? "border-brand bg-brand/10" : "border-border",
                  )}
                  onClick={() => setBgApply("one")}
                >
                  Tylko ten slajd
                </button>
                <button
                  type="button"
                  className={cn(
                    "rounded border px-2 py-0.5 text-[11px]",
                    bgApply === "all" ? "border-brand bg-brand/10" : "border-border",
                  )}
                  onClick={() => setBgApply("all")}
                >
                  Wszystkie slajdy
                </button>
              </div>
              {bgApply === "all" ? (
                <p className="mt-1 text-[11px] text-muted-foreground">
                  Zastosuj podmieni grafikę tła z tego slajdu na wszystkie (razem z przesunięciem i przybliżeniem). Tekst zostaje.
                </p>
              ) : null}
            </div>
            {mode === "bg" ? (
              <div className="min-w-0 space-y-2">
                <label className="block text-xs text-muted-foreground">
                  Przybliżenie
                  <input
                    type="range"
                    min={0.6}
                    max={2}
                    step={0.05}
                    className="mt-1 w-full"
                    value={Number(transform.scale || 1)}
                    onChange={(e) => patchBg({ scale: Number(e.target.value) })}
                  />
                </label>
                <p className="text-[11px] text-muted-foreground">
                  Proporcje zdjęcia zostają. Przeciągnij tło, żeby przesunąć kadr.
                </p>
                <div className="flex flex-wrap gap-2">
                  <Button size="sm" variant="outline" disabled={busy} onClick={() => setPickerOpen(true)}>
                    Z biblioteki
                  </Button>
                  <Button size="sm" variant="outline" disabled={busy} onClick={() => setReelOpen(true)}>
                    Z rolki
                  </Button>
                  <label className="inline-flex h-8 cursor-pointer items-center rounded-md border border-input px-3 text-xs">
                    Podmień PNG
                    <input
                      type="file"
                      accept="image/png,image/jpeg"
                      className="hidden"
                      onChange={(e) => {
                        const file = e.target.files?.[0];
                        if (file) {
                          saveM.mutate(undefined, {
                            onSuccess: () => bgM.mutate(file),
                          });
                        }
                      }}
                    />
                  </label>
                </div>
                <textarea
                  className="min-h-[72px] w-full rounded-md border border-input bg-transparent p-2 text-sm"
                  value={bgHint}
                  maxLength={400}
                  disabled={busy}
                  onChange={(e) => setBgHint(e.target.value)}
                  placeholder="np. ciepłe okno z lewej, Kasia z boku kadru, bez kuchni"
                />
                <BgPresetPicker
                  presets={bgPresets}
                  value={galleryPresetId}
                  disabled={busy}
                  mode={styleApplyMode}
                  applyDirty={stylePresetDirty}
                  applyBusy={styleApplyM.isPending}
                  onApply={() => styleApplyM.mutate()}
                  onModeChange={(m) => {
                    setStyleApplyMode(m);
                    setPickedPresetId(null);
                    api.carouselPatchTemplate(item.id, { style_apply_mode: m }).then((next) => {
                      qc.setQueryData(["carousel", item.id], next);
                    });
                  }}
                  onChange={(id) => {
                    setPickedPresetId(id);
                  }}
                />
                <BgPlacementPicker
                  placements={bgPlacements}
                  value={bgPlacementId}
                  disabled={busy}
                  onChange={(id) => {
                    setBgPlacementId(id);
                    api.carouselPatchTemplate(item.id, { bg_placement_id: id }).then((next) => {
                      qc.setQueryData(["carousel", item.id], next);
                    });
                  }}
                />
                <div className="space-y-2">
                  <Button
                    size="sm"
                    variant="outline"
                    disabled={busy || !geminiReady}
                    onClick={requestGeminiBg}
                  >
                    {bgCandM.isPending ? "Generuję 3 warianty…" : "Nowe tło Gemini (3 warianty)"}
                  </Button>
                  {bgCandM.isPending || bgCandProgress?.phase === "error" ? (
                    <BgCandidatesProgress
                      running={bgCandM.isPending}
                      progress={
                        bgCandProgress?.started_at
                          ? bgCandProgress
                          : {
                              started_at: bgCandStartedLocal.current || new Date().toISOString(),
                              total: 3,
                              done: 0,
                              current: 1,
                              phase: "waiting",
                              detail: "Start: 3 warianty tła",
                            }
                      }
                    />
                  ) : null}
                  {!geminiReady ? (
                    <p className="text-[11px] text-muted-foreground">
                      Brak klucza Gemini. Tego tła nie narysuje się drukiem.
                    </p>
                  ) : null}
                  {bgCandM.isError ? (
                    <p className="text-[11px] text-destructive">
                      {(bgCandM.error as Error)?.message || "Nie udało się wygenerować wariantów"}
                    </p>
                  ) : null}
                  {bgCandidates.length ? (
                    <div className="space-y-1.5">
                      <p className="text-[11px] text-muted-foreground">Wybierz jeden z trzech wariantów:</p>
                      <div className="grid grid-cols-3 gap-2">
                        {bgCandidates.map((cand) => (
                          <button
                            key={cand.k}
                            type="button"
                            disabled={busy}
                            className="overflow-hidden rounded-md border border-input hover:border-brand"
                            onClick={() => bgCandApplyM.mutate(cand.k)}
                          >
                            <img
                              src={`${cand.url}?v=${bgCandBust}`}
                              alt={`Wariant ${cand.k}`}
                              className="aspect-[4/5] w-full object-cover"
                            />
                            <span className="block px-1 py-0.5 text-center text-[10px]">
                              {bgCandApplyM.isPending ? "…" : `#${cand.k}`}
                            </span>
                          </button>
                        ))}
                      </div>
                    </div>
                  ) : null}
                </div>
              </div>
            ) : (
              <div className="space-y-2">
                <div className="flex flex-wrap gap-1">
                  {(layout.boxes || []).map((box) => (
                    <button
                      key={box.id}
                      type="button"
                      className={cn(
                        "rounded border px-2 py-0.5 text-[11px]",
                        selectedId === box.id ? "border-brand bg-brand/10" : "border-border",
                      )}
                      onClick={() => setSelectedId(box.id || "")}
                    >
                      {ROLE_LABEL[box.role || ""] || box.role || box.id}
                    </button>
                  ))}
                  {!visualOnly && !(layout.boxes || []).some((b) => b.id === "body" || b.role === "body") ? (
                    <button
                      type="button"
                      className="rounded border border-dashed border-border px-2 py-0.5 text-[11px] text-muted-foreground"
                      onClick={() => {
                        setLayout((prev) => {
                          const heading = (prev.boxes || []).find((b) => b.id === "heading" || b.role === "heading");
                          const body: CarouselSlideLayoutBox = {
                            id: "body",
                            role: "body",
                            text: "",
                            x: 80,
                            y: (heading?.y || 180) + (heading?.h || 360) + 16,
                            w: 920,
                            h: 280,
                            size: 60,
                            align: "center",
                            panel: false,
                            visible: true,
                            color: heading?.color || "#281f1c",
                            font: heading?.font || "source-serif",
                          };
                          const headingIdx = (prev.boxes || []).findIndex((b) => b.id === heading?.id);
                          const boxes = [...(prev.boxes || [])];
                          boxes.splice(headingIdx >= 0 ? headingIdx + 1 : boxes.length, 0, body);
                          return { ...prev, boxes };
                        });
                        setSelectedId("body");
                      }}
                    >
                      + opis
                    </button>
                  ) : null}
                  <button
                    type="button"
                    className="rounded border border-dashed border-border px-2 py-0.5 text-[11px] text-muted-foreground"
                    onClick={() => {
                      const next = defaultIconBox(layout.boxes);
                      setLayout((prev) => ({ ...prev, boxes: [...(prev.boxes || []), next] }));
                      setSelectedId(next.id || "arrow");
                    }}
                  >
                    + ikona
                  </button>
                </div>
                {selected && isIconBox(selected) ? (
                  <>
                    <IconControls
                      box={selected}
                      onPatch={(partial) => patchBox(selected.id || "arrow", partial)}
                      onRemove={() => {
                        setLayout((prev) => ({
                          ...prev,
                          boxes: (prev.boxes || []).filter((box) => box.id !== selected.id),
                        }));
                        setSelectedId((layout.boxes || []).find((b) => b.id !== selected.id)?.id || "");
                      }}
                    />
                    <RotateControls
                      box={selected}
                      onPatch={(partial) => patchBox(selected.id || "arrow", partial)}
                    />
                    {otherSlides > 0 ? (
                      <PropagateParamsBox
                        params={propagateParams}
                        onChange={setPropagateParams}
                        selectedCount={selectedPropagateParams.length}
                        busy={busy}
                        disabled={!selected.id}
                        onApply={() =>
                          propagateM.mutate({ mode: "apply", params: selectedPropagateParams })
                        }
                        onAdd={() => propagateM.mutate({ mode: "add" })}
                      />
                    ) : null}
                  </>
                ) : selected ? (
                  <>
                    <div className="flex flex-wrap items-center gap-1">
                      <FontPicker
                        fonts={composeFonts}
                        value={selected.font || "source-serif"}
                        onChange={(id) => patchBox(selected.id || "", { font: id })}
                        starredOnly
                      />
                      <ToggleBtn
                        active={Boolean(selected.bold) || styleSelected("bold")}
                        label="B"
                        title="Pogrubienie. Bez zaznaczenia idzie na cały box. Kliknij jeszcze raz, żeby zdjąć."
                        className="font-bold"
                        onClick={() => applyToSelection({ bold: true })}
                      />
                      <ToggleBtn
                        active={styleSelected("italic")}
                        label="I"
                        title="Kursywa. Bez zaznaczenia idzie na cały box. Kliknij jeszcze raz, żeby zdjąć."
                        className="italic"
                        onClick={() => applyToSelection({ italic: true })}
                      />
                      <ToggleBtn
                        active={styleSelected("underline")}
                        label="U"
                        title="Podkreślenie. Bez zaznaczenia idzie na cały box. Kliknij jeszcze raz, żeby zdjąć."
                        className="underline"
                        onClick={() => applyToSelection({ underline: true })}
                      />
                      <ToggleBtn
                        active={(selected.align || "left") === "left"}
                        label={<AlignLeft className="h-3.5 w-3.5" />}
                        title="Do lewej"
                        onClick={() => patchBox(selected.id || "", { align: "left" })}
                      />
                      <ToggleBtn
                        active={selected.align === "center"}
                        label={<AlignCenter className="h-3.5 w-3.5" />}
                        title="Do środka"
                        onClick={() => patchBox(selected.id || "", { align: "center" })}
                      />
                      <ToggleBtn
                        active={selected.align === "right"}
                        label={<AlignRight className="h-3.5 w-3.5" />}
                        title="Do prawej"
                        onClick={() => patchBox(selected.id || "", { align: "right" })}
                      />
                      <ToggleBtn
                        active={selected.align === "justify"}
                        label={<AlignJustify className="h-3.5 w-3.5" />}
                        title="Wyjustuj"
                        onClick={() => patchBox(selected.id || "", { align: "justify" })}
                      />
                      <ToggleBtn
                        active={styleSelected("highlight") || selected.highlight === "yellow"}
                        label="M"
                        title="Żółty marker. Bez zaznaczenia idzie na cały box. Kliknij jeszcze raz, żeby zdjąć."
                        className="bg-yellow-200"
                        onClick={() => applyToSelection({ highlight: "yellow" })}
                      />
                      <ToggleBtn
                        active={selected.highlight === "cream"}
                        label="K"
                        title="Kremowy marker. Bez zaznaczenia idzie na cały box. Kliknij jeszcze raz, żeby zdjąć."
                        className="bg-amber-100"
                        onClick={() => applyToSelection({ highlight: "cream" })}
                      />
                      <ToggleBtn
                        active={false}
                        label="×"
                        title="Zdejmij format. Bez zaznaczenia czyści cały box."
                        onClick={() =>
                          applyToSelection({
                            highlight: "",
                            bold: false,
                            italic: false,
                            underline: false,
                            color: "",
                          })
                        }
                      />
                    </div>
                    <div className="flex flex-wrap items-center gap-2">
                      <label className="flex items-center gap-1 text-xs text-muted-foreground">
                        Kolor tekstu
                        <input
                          type="color"
                          className="h-8 w-10 cursor-pointer rounded border border-input bg-transparent p-0"
                          value={normalizeHex(selected.color)}
                          onMouseDown={() => rememberSelection()}
                          onChange={(e) => applyToSelection({ color: e.target.value })}
                        />
                      </label>
                      {INK_SWATCHES.map((hex) => (
                        <button
                          key={hex}
                          type="button"
                          title={hex}
                          className="h-6 w-6 rounded-full border border-black/20"
                          style={{ backgroundColor: hex }}
                          onMouseDown={(e) => e.preventDefault()}
                          onClick={() => applyToSelection({ color: hex })}
                        />
                      ))}
                    </div>
                    {visualOnly && isNumBox(selected) ? (
                      <p className="text-[11px] text-muted-foreground">
                        Numer to etykieta składu, nie copy. Możesz zdjąć kropkę.
                      </p>
                    ) : null}
                    <div className="flex flex-wrap items-start gap-2">
                      <div
                        ref={editorRef}
                        contentEditable
                        suppressContentEditableWarning
                        className="min-h-[110px] min-w-[12rem] w-full flex-1 rounded-md border border-input bg-transparent p-2 text-sm leading-relaxed outline-none focus:ring-2 focus:ring-brand/30"
                        style={{
                          fontFamily: boxFontCss(selected.font),
                          color: selected.color || "#281f1c",
                          whiteSpace: "pre-wrap",
                        }}
                        onMouseDown={(e) => e.stopPropagation()}
                        onMouseUp={rememberSelection}
                        onKeyUp={rememberSelection}
                        onKeyDown={(e) => {
                          if (e.key === "Enter") {
                            e.preventDefault();
                            document.execCommand("insertText", false, "\n");
                          }
                        }}
                        onCompositionStart={() => {
                          composing.current = true;
                        }}
                        onCompositionEnd={() => {
                          composing.current = false;
                          commitEditor();
                        }}
                        onInput={() => {
                          if (!composing.current) commitEditor();
                        }}
                        onBlur={() => {
                          const live = latestBox.current;
                          if (live.id === selected.id) paintEditor(live.text, live.marks);
                        }}
                      />
                      <SlideMarkdownCheat />
                    </div>
                    <Button size="sm" variant="outline" disabled={!selected.id} onClick={toggleBulletsOnSelected}>
                      Wypunktuj linie
                    </Button>
                    <label className="block text-xs text-muted-foreground">
                      Wielkość {selected.size || 44}
                      <input
                        type="range"
                        min={14}
                        max={140}
                        className="mt-1 w-full"
                        value={selected.size || 44}
                        onChange={(e) => patchBox(selected.id || "", { size: Number(e.target.value) })}
                      />
                    </label>
                    <label className="flex items-center gap-2 text-xs">
                      <input
                        type="checkbox"
                        checked={Boolean(selected.panel)}
                        onChange={(e) =>
                          patchBox(selected.id || "", {
                            panel: e.target.checked,
                            panel_opacity: e.target.checked
                              ? selected.panel_opacity ?? DEFAULT_PANEL_OPACITY
                              : selected.panel_opacity,
                          })
                        }
                      />
                      Panel pod tekstem
                    </label>
                    {selected.panel ? (
                      <label className="block text-xs text-muted-foreground">
                        Przezroczystość tła {100 - panelOpacity(selected)}%
                        <input
                          type="range"
                          min={0}
                          max={100}
                          className="mt-1 w-full"
                          value={100 - panelOpacity(selected)}
                          onChange={(e) =>
                            patchBox(selected.id || "", {
                              panel: true,
                              panel_opacity: 100 - Number(e.target.value),
                            })
                          }
                        />
                      </label>
                    ) : null}
                    <label className="flex items-center gap-2 text-xs">
                      <input
                        type="checkbox"
                        checked={selected.visible !== false}
                        onChange={(e) => patchBox(selected.id || "", { visible: e.target.checked })}
                      />
                      Widoczny
                    </label>
                    <RotateControls
                      box={selected}
                      onPatch={(partial) => patchBox(selected.id || "", partial)}
                    />
                    {otherSlides > 0 ? (
                      <PropagateParamsBox
                        params={propagateParams}
                        onChange={setPropagateParams}
                        selectedCount={selectedPropagateParams.length}
                        busy={busy}
                        disabled={!selected.id}
                        onApply={() =>
                          propagateM.mutate({ mode: "apply", params: selectedPropagateParams })
                        }
                        onAdd={() => propagateM.mutate({ mode: "add" })}
                      />
                    ) : null}
                  </>
                ) : (
                  <p className="text-xs text-muted-foreground">Wybierz box albo kliknij go na podglądzie.</p>
                )}
              </div>
            )}
            <div className="flex flex-wrap gap-2 pt-2">
              <Button size="sm" variant="brand" disabled={busy} onClick={() => saveM.mutate()}>
                Zastosuj
              </Button>
              <Button size="sm" variant="outline" disabled={busy} onClick={() => resetM.mutate()}>
                Przywróć skład typu
              </Button>
            </div>
            {saveM.error || resetM.error || bgM.error || propagateM.error ? (
              <p className="text-xs text-destructive">
                {(saveM.error || resetM.error || bgM.error || propagateM.error) instanceof Error
                  ? ((saveM.error || resetM.error || bgM.error || propagateM.error) as Error).message
                  : "Błąd składu"}
              </p>
            ) : null}
            {propagateM.data?.propagate ? (
              <p className="text-[11px] text-muted-foreground">
                Zmieniono slajdy: {(propagateM.data.propagate.changed || []).join(", ") || "brak"}.
                {(propagateM.data.propagate.skipped || []).length
                  ? ` Pominięte: ${propagateM.data.propagate.skipped?.join(", ")}.`
                  : ""}
                {(propagateM.data.propagate.compose_failed || []).length
                  ? ` PNG nie złożył się: ${propagateM.data.propagate.compose_failed?.join(", ")}.`
                  : ""}
              </p>
            ) : null}
          </div>
        </div>
      </div>
      {reelOpen ? (
        <ReelClipModal
          carouselId={item.id}
          indexN={slide.index_n}
          apply={bgApply}
          fallback={fallback}
          onDone={() => {
            setBust(Date.now());
            setReelOpen(false);
            qc.invalidateQueries({ queryKey: ["carousel", item.id] });
          }}
          onClose={() => setReelOpen(false)}
        />
      ) : null}
      {pickerOpen ? (
        <PhotoPickerModal
          brief={{
            pain_id: item.pain_id,
            tool_id: item.tool_id,
            book_id: item.book_id,
            funnel_stage: item.funnel_stage,
            title: item.title,
            photo_kind:
              item.inspiration_borrow?.photo_kind && item.inspiration_analysis?.photo_kind
                ? item.inspiration_analysis.photo_kind
                : "",
            copy_text: carouselCopyText(item, slide),
          }}
          onPick={(asset, photoMode) => mediaM.mutate({ assetId: asset.id, photoMode })}
          onClose={() => setPickerOpen(false)}
        />
      ) : null}
    </div>
  );
}

function slideCopyBits(row: Record<string, unknown> | undefined): string[] {
  if (!row) return [];
  return ["text", "sub", "heading", "body", "caption"]
    .map((key) => String(row[key] || "").trim())
    .filter(Boolean);
}

function carouselCopyText(item: CarouselItem, slide: CarouselSlide): string {
  const parts: string[] = [item.title || "", item.caption || "", item.pain_id || ""];
  parts.push(...slideCopyBits((slide.copy || {}) as Record<string, unknown>));
  for (const box of slide.layout?.boxes || []) {
    if (box.text) parts.push(box.text);
  }
  for (const row of item.copy?.slides || []) {
    parts.push(...slideCopyBits(row));
  }
  return parts.filter(Boolean).join("\n");
}

function resizeFromHandle(
  start: { handle?: Handle; ox: number; oy: number; ow: number; oh: number },
  dx: number,
  dy: number,
  minW = MIN_BOX_W,
  minH = MIN_BOX_H,
): Partial<CarouselSlideLayoutBox> {
  let x = start.ox;
  let y = start.oy;
  let w = start.ow;
  let h = start.oh;
  const handle = start.handle || "se";
  if (handle.includes("e")) w = start.ow + dx;
  if (handle.includes("s")) h = start.oh + dy;
  if (handle.includes("w")) {
    x = start.ox + dx;
    w = start.ow - dx;
  }
  if (handle.includes("n")) {
    y = start.oy + dy;
    h = start.oh - dy;
  }
  return clampBox({ x, y, w, h }, minW, minH);
}

function starPoints(cx: number, cy: number, outer: number, inner: number) {
  return Array.from({ length: 10 }, (_, i) => {
    const ang = -Math.PI / 2 + (i * Math.PI) / 5;
    const r = i % 2 === 0 ? outer : inner;
    return `${cx + r * Math.cos(ang)},${cy + r * Math.sin(ang)}`;
  }).join(" ");
}

function heartPoints(cx: number, cy: number, rw: number, rh: number) {
  return Array.from({ length: 24 }, (_, i) => {
    const t = (i / 24) * 2 * Math.PI;
    const x = cx + (rw * 16 * Math.sin(t) ** 3) / 16;
    const y = cy - (rh * (13 * Math.cos(t) - 5 * Math.cos(2 * t) - 2 * Math.cos(3 * t) - Math.cos(4 * t))) / 16;
    return `${x},${y}`;
  }).join(" ");
}

function IconPreview({ box, compact = false }: { box: CarouselSlideLayoutBox; compact?: boolean }) {
  const w = compact ? 40 : Math.max(40, Number(box.w || 200));
  const h = compact ? 32 : Math.max(40, Number(box.h || 120));
  const color = compact ? "#281f1c" : box.color || "#b5614e";
  const style = String(box.arrow_style || "ink_double");
  if (!MARK_STYLE_IDS.has(style)) {
    const assetId = resolveArrowAssetId(style);
    const url = `/carousel-arrows/${assetId}.png`;
    return (
      <div
        className="pointer-events-none h-full w-full"
        style={{
          backgroundColor: color,
          WebkitMaskImage: `url(${url})`,
          maskImage: `url(${url})`,
          WebkitMaskSize: "contain",
          maskSize: "contain",
          WebkitMaskRepeat: "no-repeat",
          maskRepeat: "no-repeat",
          WebkitMaskPosition: "center",
          maskPosition: "center",
        }}
        aria-hidden
      />
    );
  }
  const stroke = compact ? 3.2 : Math.max(8, Math.min(28, Math.min(w, h) * 0.14));
  const pad = compact ? 3 : stroke / 2 + 4;
  const left = pad;
  const right = w - pad;
  const top = pad;
  const bot = h - pad;
  const cy = h / 2;
  const cx = w / 2;
  const spanW = right - left;
  const spanH = bot - top;
  const r = Math.min(spanW, spanH) / 2;
  return (
    <svg viewBox={`0 0 ${w} ${h}`} className="pointer-events-none h-full w-full overflow-visible" aria-hidden>
      {style === "check" ? (
        <polyline
          points={`${left + spanW * 0.08},${cy} ${left + spanW * 0.38},${bot - spanH * 0.08} ${right - spanW * 0.04},${top + spanH * 0.08}`}
          fill="none"
          stroke={color}
          strokeWidth={stroke}
          strokeLinecap="round"
          strokeLinejoin="round"
        />
      ) : style === "star" ? (
        <polygon points={starPoints(cx, cy, r, r * 0.42)} fill={color} />
      ) : style === "heart" ? (
        <polygon points={heartPoints(cx, cy + spanH * 0.06, spanW * 0.48, spanH * 0.48)} fill={color} />
      ) : style === "plus" ? (
        <>
          <line x1={left} y1={cy} x2={right} y2={cy} stroke={color} strokeWidth={stroke} strokeLinecap="round" />
          <line x1={cx} y1={top} x2={cx} y2={bot} stroke={color} strokeWidth={stroke} strokeLinecap="round" />
        </>
      ) : style === "circle" ? (
        <ellipse cx={cx} cy={cy} rx={spanW / 2} ry={spanH / 2} fill="none" stroke={color} strokeWidth={stroke} />
      ) : style === "dot" ? (
        <ellipse cx={cx} cy={cy} rx={spanW / 2} ry={spanH / 2} fill={color} />
      ) : style === "x" ? (
        <>
          <line x1={left} y1={top} x2={right} y2={bot} stroke={color} strokeWidth={stroke} strokeLinecap="round" />
          <line x1={right} y1={top} x2={left} y2={bot} stroke={color} strokeWidth={stroke} strokeLinecap="round" />
        </>
      ) : null}
    </svg>
  );
}

function PropagateParamsBox({
  params,
  onChange,
  selectedCount,
  busy,
  disabled,
  onApply,
  onAdd,
}: {
  params: Record<PropagateParamId, boolean>;
  onChange: (next: Record<PropagateParamId, boolean>) => void;
  selectedCount: number;
  busy: boolean;
  disabled: boolean;
  onApply: () => void;
  onAdd: () => void;
}) {
  return (
    <div className="space-y-2 pt-1">
      <div className="rounded-md border border-border bg-muted/30 p-2 space-y-2">
        <p className="text-[11px] font-medium text-foreground">Parametry na pozostałe slajdy</p>
        <div className="flex flex-wrap gap-x-3 gap-y-1">
          {PROPAGATE_PARAM_OPTIONS.map((row) => (
            <label key={row.id} className="flex items-center gap-1.5 text-xs">
              <input
                type="checkbox"
                checked={params[row.id]}
                onChange={(e) => onChange({ ...params, [row.id]: e.target.checked })}
              />
              {row.label}
            </label>
          ))}
        </div>
        <Button
          size="sm"
          variant="outline"
          className="w-full sm:w-auto"
          disabled={busy || disabled || selectedCount === 0}
          onClick={onApply}
        >
          Zastosuj parametry elementu do pozostałych slajdów
        </Button>
      </div>
      <Button size="sm" variant="outline" disabled={busy || disabled} onClick={onAdd}>
        Dodaj do wszystkich slajdów
      </Button>
    </div>
  );
}

function IconControls({
  box,
  onPatch,
  onRemove,
}: {
  box: CarouselSlideLayoutBox;
  onPatch: (partial: Partial<CarouselSlideLayoutBox>) => void;
  onRemove: () => void;
}) {
  const style = String(box.arrow_style || "ink_double");
  const color = normalizeHex(box.color || "#b5614e");
  return (
    <div className="space-y-3">
      <p className="text-xs text-muted-foreground">
        Strzałka albo ikona na dowolnym slajdzie. Przeciągnij na podglądzie. Obrót jest niżej.
      </p>
      <div>
        <p className="mb-1 text-xs text-muted-foreground">Strzałki</p>
        <div className="flex flex-wrap gap-2">
          {ARROW_STYLES.map((row) => (
            <button
              key={row.id}
              type="button"
              aria-pressed={style === row.id}
              title={row.label}
              className={cn(
                "flex h-14 w-14 flex-col items-center justify-center rounded-md border px-1",
                style === row.id ? "border-brand bg-brand/10" : "border-input",
              )}
              onClick={() => onPatch({ arrow_style: row.id })}
            >
              <span className="block h-7 w-8 text-[#281f1c]">
                <IconPreview box={{ ...box, arrow_style: row.id }} compact />
              </span>
              <span className="mt-0.5 text-[10px] leading-none">{row.label}</span>
            </button>
          ))}
        </div>
      </div>
      <div>
        <p className="mb-1 text-xs text-muted-foreground">Ikony</p>
        <div className="flex flex-wrap gap-2">
          {MARK_STYLES.map((row) => (
            <button
              key={row.id}
              type="button"
              aria-pressed={style === row.id}
              title={row.label}
              className={cn(
                "flex h-14 w-14 flex-col items-center justify-center rounded-md border px-1",
                style === row.id ? "border-brand bg-brand/10" : "border-input",
              )}
              onClick={() => onPatch({ arrow_style: row.id })}
            >
              <span className="block h-7 w-8 text-[#281f1c]">
                <IconPreview box={{ ...box, arrow_style: row.id }} compact />
              </span>
              <span className="mt-0.5 text-[10px] leading-none">{row.label}</span>
            </button>
          ))}
        </div>
      </div>
      <div>
        <p className="mb-1 text-xs text-muted-foreground">Kolor</p>
        <div className="flex flex-wrap items-center gap-2">
          <label className="flex items-center gap-1 text-xs text-muted-foreground">
            Dowolny
            <input
              type="color"
              className="h-8 w-10 cursor-pointer rounded border border-input bg-transparent p-0"
              value={color}
              onChange={(e) => onPatch({ color: e.target.value })}
            />
          </label>
          {ARROW_SWATCHES.map((hex) => (
            <button
              key={hex}
              type="button"
              aria-label={`Kolor ${hex}`}
              title={hex}
              className={cn(
                "h-7 w-7 rounded-full border",
                color.toLowerCase() === hex.toLowerCase() ? "border-brand ring-2 ring-brand/40" : "border-black/20",
              )}
              style={{ backgroundColor: hex }}
              onClick={() => onPatch({ color: hex })}
            />
          ))}
        </div>
      </div>
      <label className="block text-xs text-muted-foreground">
        Długość {box.h || 140}
        <input
          type="range"
          min={50}
          max={320}
          className="mt-1 w-full"
          value={box.h || 140}
          onChange={(e) => onPatch(clampBox({ ...box, h: Number(e.target.value) }, MIN_ARROW, MIN_ARROW))}
        />
      </label>
      <label className="block text-xs text-muted-foreground">
        Szerokość {box.w || 220}
        <input
          type="range"
          min={50}
          max={400}
          className="mt-1 w-full"
          value={box.w || 220}
          onChange={(e) => onPatch(clampBox({ ...box, w: Number(e.target.value) }, MIN_ARROW, MIN_ARROW))}
        />
      </label>
      <label className="flex items-center gap-2 text-xs">
        <input
          type="checkbox"
          checked={box.visible !== false}
          onChange={(e) => onPatch({ visible: e.target.checked })}
        />
        Widoczna
      </label>
      <Button size="sm" variant="outline" onClick={onRemove}>
        Usuń ikonę
      </Button>
    </div>
  );
}

function RotateControls({
  box,
  onPatch,
}: {
  box: CarouselSlideLayoutBox;
  onPatch: (partial: Partial<CarouselSlideLayoutBox>) => void;
}) {
  const deg = boxRotate(box);
  return (
    <div className="space-y-2">
      <label className="block text-xs text-muted-foreground">
        Obrót {deg}°
        <input
          type="range"
          min={0}
          max={359}
          className="mt-1 w-full"
          value={deg}
          onChange={(e) => onPatch({ rotate: Number(e.target.value) })}
        />
      </label>
      <div className="flex flex-wrap gap-1">
        {[0, 90, 180, 270].map((snap) => (
          <button
            key={snap}
            type="button"
            className={cn(
              "rounded border px-2 py-0.5 text-[11px]",
              deg === snap ? "border-brand bg-brand/10" : "border-input",
            )}
            onClick={() => onPatch({ rotate: snap })}
          >
            {snap}°
          </button>
        ))}
      </div>
    </div>
  );
}

function MarkedSlice({
  text,
  from,
  to,
  marks,
  fallbackHighlight,
}: {
  text: string;
  from: number;
  to: number;
  marks?: TextMark[];
  fallbackHighlight?: string;
}) {
  const slice = text.slice(from, to);
  const ranges =
    marks && marks.length
      ? marks
      : fallbackHighlight
        ? [{ start: from, end: to, highlight: fallbackHighlight }]
        : [];
  const cuts = new Set<number>([from, to]);
  for (const row of ranges) {
    cuts.add(Math.max(from, Number(row.start || 0)));
    cuts.add(Math.min(to, Number(row.end || 0)));
  }
  const points = [...cuts].filter((n) => n >= from && n <= to).sort((a, b) => a - b);
  if (points.length < 2) return <>{slice || "\u00a0"}</>;
  return (
    <>
      {points.slice(0, -1).map((a, i) => {
        const b = points[i + 1];
        const chunk = text.slice(a, b);
        const hit = ranges.find((row) => Number(row.start || 0) <= a && Number(row.end || 0) >= b);
        return (
          <span
            key={`${a}-${b}`}
            style={{
              backgroundColor: hit?.highlight ? highlightCss(hit.highlight) : "transparent",
              fontWeight: hit?.bold ? 700 : undefined,
              fontStyle: hit?.italic ? "italic" : undefined,
              textDecoration: hit?.underline ? "underline" : undefined,
              color: hit?.color || undefined,
            }}
          >
            {chunk}
          </span>
        );
      })}
    </>
  );
}

function MarkedPreview({
  text,
  marks,
  fallbackHighlight,
  bulletIcon = "dot",
  color = "#281f1c",
}: {
  text: string;
  marks?: TextMark[];
  fallbackHighlight?: string;
  bulletIcon?: string;
  color?: string;
}) {
  const lines = String(text || "").split("\n");
  let offset = 0;
  return (
    <span className="block">
      {lines.map((raw, i) => {
        const parsed = splitBulletPrefix(raw);
        const from = offset + parsed.prefixLen;
        const to = from + parsed.rest.length;
        const body = (
          <MarkedSlice text={text} from={from} to={to} marks={marks} fallbackHighlight={fallbackHighlight} />
        );
        offset += raw.length + 1;
        if (!parsed.bullet) {
          return (
            <span key={i} className="block">
              {body}
            </span>
          );
        }
        return (
          <span key={i} className="flex items-start" style={{ gap: "0.2em" }}>
            <span className="mt-[0.18em] inline-block h-[0.7em] w-[0.7em] shrink-0" aria-hidden>
              <IconPreview box={{ arrow_style: normalizeBulletIcon(bulletIcon), color, w: 20, h: 20 }} compact />
            </span>
            <span className="min-w-0 flex-1">{body}</span>
          </span>
        );
      })}
    </span>
  );
}

function SlideMarkdownCheat() {
  const lines = STUDIO_TIPS.slideMarkdown.split("\n").filter(Boolean);
  return (
    <aside className="w-[220px] max-w-full shrink-0 grow rounded-md border border-border bg-muted/40 p-2 text-[11px] leading-snug text-muted-foreground">
      <p className="mb-1 font-medium text-foreground">Markdown w szkicu</p>
      <ul className="space-y-1">
        {lines.map((line) => (
          <li key={line}>{line}</li>
        ))}
      </ul>
    </aside>
  );
}

function ToggleBtn({
  active,
  label,
  title,
  className,
  onClick,
}: {
  active: boolean;
  label: ReactNode;
  title: string;
  className?: string;
  onClick: () => void;
}) {
  return (
    <button
      type="button"
      title={title}
      className={cn(
        "inline-flex h-8 w-8 items-center justify-center rounded-md border text-xs",
        active ? "border-brand bg-brand/10" : "border-input",
        className,
      )}
      onMouseDown={(e) => e.preventDefault()}
      onClick={onClick}
    >
      {label}
    </button>
  );
}
